// This file is auto-generated by @hey-api/openapi-ts

import { client } from './Client.gen';
import type { EshopAuthControllerConfirmationData } from './EshopAuthControllerConfirmationData.gen';
import type { EshopAuthControllerConfirmationErrors } from './EshopAuthControllerConfirmationErrors.gen';
import type { EshopAuthControllerConfirmationResponses } from './EshopAuthControllerConfirmationResponses.gen';
import type { EshopAuthControllerGetCurrentPasswordRequiredData } from './EshopAuthControllerGetCurrentPasswordRequiredData.gen';
import type { EshopAuthControllerGetCurrentPasswordRequiredErrors } from './EshopAuthControllerGetCurrentPasswordRequiredErrors.gen';
import type { EshopAuthControllerGetCurrentPasswordRequiredResponses } from './EshopAuthControllerGetCurrentPasswordRequiredResponses.gen';
import type { EshopAuthControllerChangePasswordData } from './EshopAuthControllerChangePasswordData.gen';
import type { EshopAuthControllerChangePasswordErrors } from './EshopAuthControllerChangePasswordErrors.gen';
import type { EshopAuthControllerChangePasswordResponses } from './EshopAuthControllerChangePasswordResponses.gen';
import type { EshopAuthControllerLoginData } from './EshopAuthControllerLoginData.gen';
import type { EshopAuthControllerLoginErrors } from './EshopAuthControllerLoginErrors.gen';
import type { EshopAuthControllerLoginResponses } from './EshopAuthControllerLoginResponses.gen';
import type { EshopAuthControllerMapPhoneLoginData } from './EshopAuthControllerMapPhoneLoginData.gen';
import type { EshopAuthControllerMapPhoneLoginErrors } from './EshopAuthControllerMapPhoneLoginErrors.gen';
import type { EshopAuthControllerMapPhoneLoginResponses } from './EshopAuthControllerMapPhoneLoginResponses.gen';
import type { EshopAuthControllerMapPhoneResetPasswordData } from './EshopAuthControllerMapPhoneResetPasswordData.gen';
import type { EshopAuthControllerMapPhoneResetPasswordErrors } from './EshopAuthControllerMapPhoneResetPasswordErrors.gen';
import type { EshopAuthControllerMapPhoneResetPasswordResponses } from './EshopAuthControllerMapPhoneResetPasswordResponses.gen';
import type { EshopAuthControllerParentConfirmationData } from './EshopAuthControllerParentConfirmationData.gen';
import type { EshopAuthControllerParentConfirmationErrors } from './EshopAuthControllerParentConfirmationErrors.gen';
import type { EshopAuthControllerParentConfirmationResponses } from './EshopAuthControllerParentConfirmationResponses.gen';
import type { EshopAuthControllerRegisterData } from './EshopAuthControllerRegisterData.gen';
import type { EshopAuthControllerRegisterErrors } from './EshopAuthControllerRegisterErrors.gen';
import type { EshopAuthControllerRegisterResponses } from './EshopAuthControllerRegisterResponses.gen';
import type { EshopAuthControllerResendData } from './EshopAuthControllerResendData.gen';
import type { EshopAuthControllerResendErrors } from './EshopAuthControllerResendErrors.gen';
import type { EshopAuthControllerResendResponses } from './EshopAuthControllerResendResponses.gen';
import type { EshopAuthControllerResetPasswordData } from './EshopAuthControllerResetPasswordData.gen';
import type { EshopAuthControllerResetPasswordErrors } from './EshopAuthControllerResetPasswordErrors.gen';
import type { EshopAuthControllerResetPasswordResponses } from './EshopAuthControllerResetPasswordResponses.gen';
import type { EshopAuthControllerUpdatePasswordData } from './EshopAuthControllerUpdatePasswordData.gen';
import type { EshopAuthControllerUpdatePasswordErrors } from './EshopAuthControllerUpdatePasswordErrors.gen';
import type { EshopAuthControllerUpdatePasswordResponses } from './EshopAuthControllerUpdatePasswordResponses.gen';
import type { Options } from './Options.gen';

export class EshopAuthController {
    /**
     * Register a customer.
     *
     * Creates unverified customer account.<br>
     * Send a confirmation link to the given email.<br>
     * ### Error names for HTTP Status 400 BadRequest:<br>
     * + **VALIDATION_ERROR** - Invalid body, check 'errors' property for more info.<br>
     * + **USERNAME_NOT_UNIQUE** - Username already exists.<br>
     * + **TERMS_AND_CONDITIONS_NOT_AGREED_TO** - To register, the terms and conditions must be agreed to.<br>
     * + **REGISTRATION_UNDER_15_MISSING_PROPERTIES** - Registration under 15 years request is missing "isUnder15Years" property.<br>
     * + **REGISTRATION_UNDER_15_NOT_ALLOWED** - Registration under 15 years is not allowed in system configuration.<br>
     * + **USERNAME_AND_PARENT_EMAIL_CAN_NOT_BE_SAME** - Username and parent email can not be same.<br>
     * + **PARENT_FOR_OLDER_THAN_15_YEARS_NOT_ALLOWED** - Parent email can not be set for users older than 15 years.<br>
     * + **CUSTOMER_CARD_ALREADY_EXISTS** - Customer card already exists.<br>
     * + **REGULAR_CUSTOMER_PROFILE_NOT_FOUND** - Regular customer profile does not exist.
     */
    public static register<ThrowOnError extends boolean = false>(options: Options<EshopAuthControllerRegisterData, ThrowOnError>) {
        return (options.client ?? client).post<EshopAuthControllerRegisterResponses, EshopAuthControllerRegisterErrors, ThrowOnError>({
            url: '/api/eshop/auth/register',
            ...options,
            headers: {
                'Content-Type': 'application/json',
                ...options.headers
            }
        });
    }
    
    /**
     * Resend a verification code for the given email.
     *
     * Body contains an email.<br>
     * ### Error names for HTTP Status 400 BadRequest:<br>
     * + **VALIDATION_ERROR** - Invalid body, check 'errors' property for more info.<br>
     * + **INVALID_EMAIL** - Email does not exists or customer account is already verified.
     */
    public static resend<ThrowOnError extends boolean = false>(options: Options<EshopAuthControllerResendData, ThrowOnError>) {
        return (options.client ?? client).post<EshopAuthControllerResendResponses, EshopAuthControllerResendErrors, ThrowOnError>({
            url: '/api/eshop/auth/resend',
            ...options,
            headers: {
                'Content-Type': 'application/json',
                ...options.headers
            }
        });
    }
    
    /**
     * Confirm a customer account.
     *
     * Verify a customer account based on token.<br>
     * Body contains a token.<br>
     * ### Error names for HTTP Status 400 BadRequest:<br>
     * + **VALIDATION_ERROR** - Invalid body, check 'errors' property for more info.<br>
     * + **INVALID_VERIFICATION_TOKEN** - Token is invalid or has already expired.
     */
    public static confirmation<ThrowOnError extends boolean = false>(options: Options<EshopAuthControllerConfirmationData, ThrowOnError>) {
        return (options.client ?? client).post<EshopAuthControllerConfirmationResponses, EshopAuthControllerConfirmationErrors, ThrowOnError>({
            url: '/api/eshop/auth/confirmation',
            ...options,
            headers: {
                'Content-Type': 'application/json',
                ...options.headers
            }
        });
    }
    
    /**
     * Confirm a customer's parent account.
     *
     * Verify a customer account based on token.<br>
     * Body contains a token.<br>
     * ### Error names for HTTP Status 400 BadRequest:<br>
     * + **VALIDATION_ERROR** - Invalid body, check 'errors' property for more info.<br>
     * + **INVALID_VERIFICATION_TOKEN** - Token is invalid or has already expired.<br>
     * + **REGISTRATION_UNDER_15_NOT_ALLOWED** - Registration under 15 years is not allowed in system configuration.
     */
    public static parentConfirmation<ThrowOnError extends boolean = false>(options: Options<EshopAuthControllerParentConfirmationData, ThrowOnError>) {
        return (options.client ?? client).post<EshopAuthControllerParentConfirmationResponses, EshopAuthControllerParentConfirmationErrors, ThrowOnError>({
            url: '/api/eshop/auth/parent-confirmation',
            ...options,
            headers: {
                'Content-Type': 'application/json',
                ...options.headers
            }
        });
    }
    
    /**
     * Authenticate a customer
     *
     * ### Error names for HTTP Status 400 BadRequest:<br>
     * + **VALIDATION_ERROR** - Invalid body, check 'errors' property for more info.<br>
     * + **INVALID_CREDENTIALS** - Username, cardNumber or password is invalid. (also can mean: card is blocked, expired or not valid yet, user is disabled).<br>
     * + **CUSTOMER_PARENT_NOT_VERIFIED** - Customer parent email is not verified.<br>
     * + **CUSTOMER_ACCOUNT_NOT_VERIFIED** - Customer account is not verified.<br>
     * + **CUSTOMER_CARD_ALREADY_EXISTS** - Customer card already exists.<br>
     * + **REGULAR_CUSTOMER_PROFILE_NOT_FOUND** - Regular customer profile does not exist.<br>
     * + **ACCOUNT_RESTRICTED_TO_MOBILE_ACCESS** - This account can only be accessed via the mobile app.
     */
    public static login<ThrowOnError extends boolean = false>(options: Options<EshopAuthControllerLoginData, ThrowOnError>) {
        return (options.client ?? client).post<EshopAuthControllerLoginResponses, EshopAuthControllerLoginErrors, ThrowOnError>({
            url: '/api/eshop/auth/login',
            ...options,
            headers: {
                'Content-Type': 'application/json',
                ...options.headers
            }
        });
    }
    
    /**
     * Authenticate a map phone customer
     *
     * For login is required set header: **map-phone-auth-token**<br>
     * ### Error names for HTTP Status 400 BadRequest:<br>
     * + **VALIDATION_ERROR** - Invalid body, check 'errors' property for more info.<br>
     * + **INVALID_CREDENTIALS** - Username, cardNumber or password is invalid. (also can mean: card is blocked, expired or not valid yet, user is disabled).<br>
     * + **CUSTOMER_ACCOUNT_NOT_VERIFIED** - Customer account is not verified.
     */
    public static mapPhoneLogin<ThrowOnError extends boolean = false>(options: Options<EshopAuthControllerMapPhoneLoginData, ThrowOnError>) {
        return (options.client ?? client).post<EshopAuthControllerMapPhoneLoginResponses, EshopAuthControllerMapPhoneLoginErrors, ThrowOnError>({
            url: '/api/eshop/auth/map-phone-login',
            ...options,
            headers: {
                'Content-Type': 'application/json',
                ...options.headers
            }
        });
    }
    
    /**
     * Request a password reset.
     *
     * Send a reset password link to the given email.<br>
     * Body contains an email.<br>
     * ### Error names for HTTP Status 400 BadRequest:<br>
     * + **VALIDATION_ERROR** - Invalid body, check 'errors' property for more info.<br>
     * + **INVALID_EMAIL** - Email does not exists or customer account is already verified.
     */
    public static resetPassword<ThrowOnError extends boolean = false>(options: Options<EshopAuthControllerResetPasswordData, ThrowOnError>) {
        return (options.client ?? client).post<EshopAuthControllerResetPasswordResponses, EshopAuthControllerResetPasswordErrors, ThrowOnError>({
            url: '/api/eshop/auth/reset-password',
            ...options,
            headers: {
                'Content-Type': 'application/json',
                ...options.headers
            }
        });
    }
    
    /**
     * Request a map phone password reset.
     *
     * For reset password is required set header: **map-phone-auth-token**<br>
     * Send a reset password link to the given email.<br>
     * Body contains an email.<br>
     * ### Error names for HTTP Status 400 BadRequest:<br>
     * + **VALIDATION_ERROR** - Invalid body, check 'errors' property for more info.<br>
     * + **INVALID_EMAIL** - Email does not exists or customer account is already verified.
     */
    public static mapPhoneResetPassword<ThrowOnError extends boolean = false>(options: Options<EshopAuthControllerMapPhoneResetPasswordData, ThrowOnError>) {
        return (options.client ?? client).post<EshopAuthControllerMapPhoneResetPasswordResponses, EshopAuthControllerMapPhoneResetPasswordErrors, ThrowOnError>({
            url: '/api/eshop/auth/map-phone-reset-password',
            ...options,
            headers: {
                'Content-Type': 'application/json',
                ...options.headers
            }
        });
    }
    
    /**
     * Change a password for the given token.
     *
     * ### Error names for HTTP Status 400 BadRequest:<br>
     * + **VALIDATION_ERROR** - Invalid body, check 'errors' property for more info.<br>
     * + **INVALID_VERIFICATION_TOKEN** - Token is invalid or has already expired.
     */
    public static changePassword<ThrowOnError extends boolean = false>(options: Options<EshopAuthControllerChangePasswordData, ThrowOnError>) {
        return (options.client ?? client).post<EshopAuthControllerChangePasswordResponses, EshopAuthControllerChangePasswordErrors, ThrowOnError>({
            url: '/api/eshop/auth/change-password',
            ...options,
            headers: {
                'Content-Type': 'application/json',
                ...options.headers
            }
        });
    }
    
    /**
     * Change user password.
     *
     * ### Error names for HTTP Status 400 BadRequest:<br>
     * + **VALIDATION_ERROR** - Invalid body, check 'errors' property for more info.<br>
     * + **INVALID_VERIFICATION_TOKEN** - Token is invalid or has already expired.
     */
    public static updatePassword<ThrowOnError extends boolean = false>(options: Options<EshopAuthControllerUpdatePasswordData, ThrowOnError>) {
        return (options.client ?? client).post<EshopAuthControllerUpdatePasswordResponses, EshopAuthControllerUpdatePasswordErrors, ThrowOnError>({
            url: '/api/eshop/auth/update-password',
            ...options,
            headers: {
                'Content-Type': 'application/json',
                ...options.headers
            }
        });
    }
    
    /**
     * Check if current password is required when updating password
     */
    public static getCurrentPasswordRequired<ThrowOnError extends boolean = false>(options?: Options<EshopAuthControllerGetCurrentPasswordRequiredData, ThrowOnError>) {
        return (options?.client ?? client).get<EshopAuthControllerGetCurrentPasswordRequiredResponses, EshopAuthControllerGetCurrentPasswordRequiredErrors, ThrowOnError>({ url: '/api/eshop/auth/current-password-required', ...options });
    }
}
